import { computed, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { get, isBoolean, isNil, isObject } from 'lodash';

import { DatabaseEntities, YardTaskStatus } from '@nova/core';
import { isCheckTask, isMoveTask } from '@satellite/helpers/yardTaskUtils';
import { useEventHub } from '@satellite/composables/useEventHub';
import yardTasksService from '@satellite/services/yard-tasks-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import { hasCollapsedSocketEventIds } from '../helpers/socketEventMatch';
import {
  EntityPresenterMode,
  isEntityPresenterQueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterEntityMode';

import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from './presenterPlaceholder';
import type { YardTaskDetails } from './yardTaskPresenterShared';

type RelatedEntityOption =
  | DatabaseEntities.Spot
  | DatabaseEntities.AssetContainer
  | Ref<DatabaseEntities.Spot | DatabaseEntities.AssetContainer>
  | ComputedRef<DatabaseEntities.Spot | DatabaseEntities.AssetContainer>;

export interface UseYardTaskByRelatedEntityFetcherOptions {
  relatedEntity: RelatedEntityOption;
  relatedIds: MaybeRefOrGetter<string[]>;
  statuses?: MaybeRefOrGetter<YardTaskStatus[]>;
  /** Restrict to a single task type ('move' | 'check'); omit to fetch all task types in one request. */
  type?: MaybeRefOrGetter<string>;
  initialTaskListData?:
    | Ref<Maybe<YardTaskDetails[]>>
    | ComputedRef<Maybe<YardTaskDetails[]>>
    | YardTaskDetails[];
  placeholderTaskListData?:
    | Ref<Maybe<PresenterPlaceholderData<YardTaskDetails>[]>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardTaskDetails>[]>>
    | PresenterPlaceholderData<YardTaskDetails>[];
  staticTaskListData?:
    | Ref<Maybe<YardTaskDetails[]>>
    | ComputedRef<Maybe<YardTaskDetails[]>>
    | YardTaskDetails[];
  headless?: boolean;
  mode?: EntityPresenterMode;
  staleTime?: number;
}

function toNormalizedIds(value: Maybe<string[]>): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((id): id is string => Boolean(id)).sort();
}

function toNormalizedStatuses(value: Maybe<YardTaskStatus[]>): YardTaskStatus[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [YardTaskStatus.CREATED, YardTaskStatus.IN_PROGRESS];
  }

  return [...value].sort();
}

export function useYardTaskByRelatedEntityFetcher(
  options: UseYardTaskByRelatedEntityFetcherOptions
) {
  const {
    relatedEntity,
    relatedIds,
    statuses: statusesOption = [YardTaskStatus.CREATED, YardTaskStatus.IN_PROGRESS],
    type: taskTypeOption,
    initialTaskListData,
    placeholderTaskListData,
    staticTaskListData,
    headless = false,
    mode = EntityPresenterMode.Live,
    staleTime = Infinity
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);
  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));

  const effectiveRelatedEntity = computed(() => unref(relatedEntity));
  const effectiveRelatedIds = computed(() => toNormalizedIds(toValue(relatedIds)));
  const effectiveStatuses = computed(() => toNormalizedStatuses(toValue(statusesOption)));
  const effectiveTaskType = computed(() => toValue(taskTypeOption) ?? null);
  const taskTypeKey = computed(() => effectiveTaskType.value ?? 'all');
  const initialTaskList = computed(() => toValue(initialTaskListData) ?? undefined);
  const placeholderTaskList = computed(() => toValue(placeholderTaskListData) ?? undefined);
  const staticTaskList = computed(() => toValue(staticTaskListData) ?? undefined);
  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  if (
    isStaticMode(mode) &&
    (!isNil(toValue(initialTaskListData)) || !isNil(toValue(placeholderTaskListData)))
  ) {
    console.warn(
      '[useYardTaskByRelatedEntityFetcher] mode=static ignores initialTaskListData/placeholderTaskListData. Use staticTaskListData.'
    );
  }
  if (isLazyLiveMode(mode) && !isNil(toValue(initialTaskListData))) {
    console.warn(
      '[useYardTaskByRelatedEntityFetcher] mode=lazy-live does not use initialTaskListData for the query cache; use placeholderTaskListData for partial UI.'
    );
  }

  const keyRelatedIds = computed(() => effectiveRelatedIds.value.join(',') || 'none');
  const keyStatuses = computed(() => effectiveStatuses.value.join(','));

  const isTaskRelated = (task: PresenterPlaceholderData<YardTaskDetails>): boolean => {
    const ids = effectiveRelatedIds.value;
    if (ids.length === 0) {
      return false;
    }

    if (isMoveTask(task)) {
      const taskAssetContainerId =
        get(task, 'child.assetContainerId') ||
        get(task, 'child.assetContainer.id') ||
        get(task, 'assetContainer.id');
      const taskOriginSpotId = get(task, 'child.originSpotId') || get(task, 'child.originSpot.id');
      const taskDestinationSpotId =
        get(task, 'child.destinationSpotId') || get(task, 'child.destinationSpot.id');

      if (effectiveRelatedEntity.value === DatabaseEntities.Spot) {
        return ids.includes(taskOriginSpotId) || ids.includes(taskDestinationSpotId);
      }

      return ids.includes(taskAssetContainerId);
    }

    if (isCheckTask(task)) {
      const taskRelatedId: string = get(task, 'child.relatedId');
      const taskRelatedEntity: DatabaseEntities = get(task, 'child.relatedEntity');
      return taskRelatedEntity === effectiveRelatedEntity.value && ids.includes(taskRelatedId);
    }

    return false;
  };

  const shouldIncludeTaskByStatus = (task: PresenterPlaceholderData<YardTaskDetails>) =>
    effectiveStatuses.value.includes(task.status as YardTaskStatus);

  const filterRelatedTasks = (allTasks: YardTaskDetails[]) =>
    allTasks.filter(task => isTaskRelated(task) && shouldIncludeTaskByStatus(task));

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = isBoolean(entry)
      ? entry
      : isObject(entry)
        ? (entry.isIntersecting ?? true)
        : true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const taskListQuery = useQuery({
    queryKey: computed(() =>
      effectiveRelatedIds.value.length > 0
        ? queryKeys.tasksByRelatedEntityList(
            effectiveRelatedEntity.value,
            keyRelatedIds.value,
            keyStatuses.value,
            taskTypeKey.value
          )
        : []
    ),
    enabled: computed(() =>
      isEntityPresenterQueryEnabled({
        mode,
        hasEffectiveId: effectiveRelatedIds.value.length > 0,
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialTaskList.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderTaskList.value as YardTaskDetails[] | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      if (effectiveRelatedIds.value.length === 0) {
        return [] as YardTaskDetails[];
      }

      const response = await yardTasksService.getYardTaskList({
        requestConfig: {
          signal,
          params: {
            relatedEntity: effectiveRelatedEntity.value,
            relatedIds: effectiveRelatedIds.value.join(','),
            statuses: effectiveStatuses.value.join(','),
            ...(effectiveTaskType.value ? { type: effectiveTaskType.value } : {})
          }
        },
        enableCache: true
      });

      return filterRelatedTasks(get(response, 'items', []) as YardTaskDetails[]);
    }
  });

  const tasks = ref<YardTaskDetails[]>([]);

  watch(
    () => taskListQuery.data.value,
    newData => {
      tasks.value = (newData as YardTaskDetails[] | undefined) ?? [];
    },
    { immediate: true }
  );

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();

    if (effectiveRelatedIds.value.length === 0) {
      return;
    }

    queryClient.invalidateQueries(
      {
        queryKey: queryKeys.tasksByRelatedEntityList(
          effectiveRelatedEntity.value,
          keyRelatedIds.value,
          keyStatuses.value,
          taskTypeKey.value
        )
      },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();
    await taskListQuery.refetch();
  };

  /**
   * `SocketPlugin` debounces by 100ms and forwards only the last payload, so one action that writes
   * several tasks — completing a move also flips the blocked flag on the tasks that wanted its spot —
   * arrives as a single event carrying somebody else's task, with the rest under `debounce.ids`.
   * Reading that payload and deciding "not my spot" dropped the update entirely, which is why a
   * started move only appeared here after a reload (OD-4007). A collapsed event describes a task we
   * cannot inspect, so the only safe response is to refetch (OD-3998 does the same for one task).
   */
  const isCollapsedEvent = (task: PresenterPlaceholderData<YardTaskDetails>): boolean =>
    hasCollapsedSocketEventIds(task);

  const refetchForCollapsedEvent = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleCreateYardTask = (
    task: PresenterPlaceholderData<YardTaskDetails> & { constructor: unknown }
  ) => {
    if (isCollapsedEvent(task)) {
      refetchForCollapsedEvent();
      return;
    }

    if (!task?.id || !isTaskRelated(task) || !shouldIncludeTaskByStatus(task)) {
      return;
    }
    wakeLazyLive();

    tasks.value = [...tasks.value, task as YardTaskDetails];

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleUpdateYardTask = (
    task: PresenterPlaceholderData<YardTaskDetails> & { constructor: unknown }
  ) => {
    if (!task?.id) {
      return;
    }

    if (isCollapsedEvent(task)) {
      refetchForCollapsedEvent();
      return;
    }

    const list = tasks.value;
    const index = list.findIndex(item => item.id === task.id);
    const isRelated = isTaskRelated(task);
    const matchesStatus = shouldIncludeTaskByStatus(task);

    wakeLazyLive();

    if (!isRelated || !matchesStatus) {
      if (index >= 0) {
        tasks.value = [...list.slice(0, index), ...list.slice(index + 1)];
      }
    } else if (index >= 0) {
      tasks.value = [...list.slice(0, index), task as YardTaskDetails, ...list.slice(index + 1)];
    } else {
      tasks.value = [...list, task as YardTaskDetails];
    }

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleDeleteYardTask = (
    task: PresenterPlaceholderData<YardTaskDetails> & { constructor: unknown }
  ) => {
    if (!task?.id) {
      return;
    }

    if (isCollapsedEvent(task)) {
      refetchForCollapsedEvent();
      return;
    }

    wakeLazyLive();

    tasks.value = tasks.value.filter(item => item.id !== task.id);

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  watch(
    [effectiveRelatedEntity, effectiveRelatedIds, effectiveStatuses, effectiveTaskType],
    (newValues, oldValues) => {
      if (!oldValues) {
        return;
      }

      const hasChanged = JSON.stringify(newValues) !== JSON.stringify(oldValues);
      if (!hasChanged) {
        return;
      }

      tasks.value = [];
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  );

  onMounted(() => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    eventHub.on('create-YardTask', handleCreateYardTask);
    eventHub.on('update-YardTask', handleUpdateYardTask);
    eventHub.on('delete-YardTask', handleDeleteYardTask);
  });

  onScopeDispose(() => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    eventHub.off('create-YardTask', handleCreateYardTask);
    eventHub.off('update-YardTask', handleUpdateYardTask);
    eventHub.off('delete-YardTask', handleDeleteYardTask);
  });

  const data = computed(() => {
    const currentData = isStaticMode(mode) ? staticTaskList.value : tasks.value;

    if (!currentData) {
      if (isStaticMode(mode)) {
        console.warn(
          '[useYardTaskByRelatedEntityFetcher] mode=static requires staticTaskListData.'
        );
      }

      return [];
    }

    return currentData;
  });
  const isLoading = computed(() => (isStaticMode(mode) ? false : taskListQuery.isLoading.value));
  const isFetching = computed(() => (isStaticMode(mode) ? false : taskListQuery.isFetching.value));
  const error = computed(() => (isStaticMode(mode) ? null : taskListQuery.error.value));
  const getFirstTask = () => data.value[0] || null;

  return {
    data,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,
    getFirstTask,

    onIntersect: handleIntersect,

    query: taskListQuery
  };
}
